import { memo, MouseEvent, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Button } from "@design-system/Button/Button";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import { useScope } from "@state/scopeContext.lib";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Menu } from "@design-system/Menu/Menu.lib";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { HStack } from "@design-system/HStack/HStack";
import { ClientsWorkOverviewRow } from "../clientsWorkOverviewViewConfig";
import {
    type ClientsWorkOverviewCallbacks,
    useWorkRowActions,
    WorkRowActionItems,
} from "../WorkRowActions";

type Meta = ViewTableMeta<ClientsWorkOverviewCallbacks, never>;

/**
 * A row's hover actions: Open on a client row switches to its workspace; a
 * work row has its actions menu (Open details, View in Actions, Copy link, Delete…).
 */
export const WorkActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, unknown>) => {
        const { changeActiveWorkspace } = useScope();
        const { onConfirmationRequest, confirmationModal } =
            useWorkRowActions();
        const workspaceId =
            row.original.rowType === "workspace"
                ? row.original.workspaceId
                : null;
        const meta = table.options.meta as Meta | undefined;

        const handleOpenWorkspace = useCallback(
            (e: MouseEvent<HTMLElement>) => {
                markEventBlocked(e);
                if (workspaceId) {
                    changeActiveWorkspace(
                        workspaceId,
                        OrganizationMemberClientAccessSource.CLIENTS_LIST,
                    );
                }
            },
            [changeActiveWorkspace, workspaceId],
        );

        if (workspaceId) {
            return (
                <ViewTableCell captureClicks>
                    <HStack gap={12} className={tableStyles.showOnHover}>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleOpenWorkspace}
                        >
                            Open
                        </Button>
                    </HStack>
                </ViewTableCell>
            );
        }

        if (
            row.original.rowType !== "work" ||
            !parseCloseId(row.original.closeId) ||
            isBusyClosesDemoId(row.original.closeId)
        ) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <HStack gap={12} className={tableStyles.showOnHover}>
                    <Menu.More className={tableStyles.showOnHover}>
                        <WorkRowActionItems
                            row={row.original}
                            callbacks={meta?.callbacks}
                            onConfirmationRequest={onConfirmationRequest}
                        />
                    </Menu.More>
                </HStack>
                {confirmationModal}
            </ViewTableCell>
        );
    },
);

WorkActionsCell.displayName = "WorkActionsCell";
